/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {createParser} from 'nuqs';

import {splitEvery, values} from 'ramda';

import {
  DEFAULT_ROW_INDEX,
  DataGridSearchParams,
  GRID_ENTRY_SEPARATOR,
  PAGE_ENTRY_SEPARATOR,
  PAGE_GRID_SEPARATOR,
} from '../../constants/urlSync';

/**
 * Entries with missing, empty, or literal `'undefined'` queryId are skipped during
 * serialization — this prevents ghost entries like `1,undefined,1` from polluting the URL.
 */
const isValidGridEntry = (entry: DataGridSearchParams): boolean =>
  Boolean(entry.gridCode) && Boolean(entry.queryId) && entry.queryId !== 'undefined';

/** Builds a grid entry from `[gridCode, queryId, rowIndex]` parts, keyed by gridCode. */
const addGridEntryFromParts = (
  result: Record<string, DataGridSearchParams>,
  parts: string[]
): void => {
  if (parts[0]) {
    result[parts[0]] = {gridCode: parts[0], queryId: parts[1], rowIndex: parts[2]};
  }
};

/**
 * Compound parser for the `dataGrids` query param. Multiple grid instances share this
 * single key — each grid owns its own slice keyed by `gridCode`.
 *
 * URL format: `vehicle,abc,1|customers,def,2|` (3 fields per entry, pipe-separated,
 * trailing pipe so the parser always recognises the new format even for single entries).
 *
 * Legacy comma-only format `vehicle,abc,1,customers,def,2` is still accepted on read
 * (auto-detected when no pipe is present) to keep old bookmarks working.
 */
export const dataGridsParser = createParser<Record<string, DataGridSearchParams>>({
  parse(rawValue) {
    if (!rawValue) {
      return {};
    }

    const result: Record<string, DataGridSearchParams> = {};

    if (rawValue.includes(GRID_ENTRY_SEPARATOR)) {
      rawValue
        .split(GRID_ENTRY_SEPARATOR)
        .forEach((entry) => addGridEntryFromParts(result, entry.split(',')));
      return result;
    }

    splitEvery(3)(rawValue.split(',')).forEach((parts) => addGridEntryFromParts(result, parts));
    return result;
  },
  serialize(record) {
    const entries = values(record)
      .filter(isValidGridEntry)
      .map((entry) => `${entry.gridCode},${entry.queryId},${entry.rowIndex ?? DEFAULT_ROW_INDEX}`);

    return entries.length > 0 ? entries.join(GRID_ENTRY_SEPARATOR) + GRID_ENTRY_SEPARATOR : '';
  },
});

/**
 * Compound parser for the `page` query param. Multiple grid instances share this key.
 *
 * URL format: `vehicle:2,customers:1` (gridCode:N pairs, comma-separated).
 */
export const pageParser = createParser<Record<string, number>>({
  parse(rawValue) {
    if (!rawValue) {
      return {};
    }

    const result: Record<string, number> = {};

    rawValue.split(PAGE_GRID_SEPARATOR).forEach((entry) => {
      const separatorIndex = entry.indexOf(PAGE_ENTRY_SEPARATOR);
      if (separatorIndex === -1) {
        return;
      }
      const code = entry.slice(0, separatorIndex);
      const parsed = parseInt(entry.slice(separatorIndex + 1), 10);
      if (code && !isNaN(parsed)) {
        result[code] = parsed;
      }
    });

    return result;
  },
  serialize(record) {
    return Object.entries(record)
      .map(([code, page]) => `${code}${PAGE_ENTRY_SEPARATOR}${page}`)
      .join(PAGE_GRID_SEPARATOR);
  },
});

/**
 * Parser for the `sourceDataGrid` query param — single grid context preserved across
 * detail navigation. URL format: `gridCode,queryId,rowIndex`.
 *
 * Returns `null` for an absent/empty value; partial values (1 or 2 fields) yield an
 * object with the missing fields set to `undefined`, matching the historical contract
 * of `getFormattedSourceSearchParams`.
 */
export const sourceDataGridParser = createParser<DataGridSearchParams>({
  parse(rawValue) {
    if (!rawValue) {
      return null;
    }
    const parts = rawValue.split(',');
    return {
      gridCode: parts[0] as string,
      queryId: parts[1] as string,
      rowIndex: parts[2] as string,
    };
  },
  serialize(value) {
    return `${value.gridCode ?? ''},${value.queryId ?? ''},${value.rowIndex ?? ''}`;
  },
});
